import { 
  TranscribeStreamingClient
} from "@aws-sdk/client-transcribe-streaming";
import { GetAWSCredentials } from '../../api';

// Handle Dragon text selection and replacement commands on the text itself
const handleTextCommands = (text) => {
  let processedText = text;
  
  // Handle "select [word] delete" commands - only match complete commands
  const selectDeleteMatches = [...processedText.matchAll(/\bselect\s+(\w+)\s+delete\b/gi)];
  for (const match of selectDeleteMatches) {
    const wordToDelete = match[1].trim();
    // Remove the word from the text (before the command)
    processedText = processedText.replace(new RegExp(`\\b${wordToDelete}\\b`, 'gi'), '');
    // Remove the command itself
    processedText = processedText.replace(match[0], '');
  }
  
  // Handle "select [word] replace with [newword]" commands - only match complete commands
  const selectReplaceMatches = [...processedText.matchAll(/\bselect\s+(\w+)\s+replace\s+with\s+(\w+)\b/gi)];
  for (const match of selectReplaceMatches) {
    const oldWord = match[1].trim();
    const newWord = match[2].trim();
    // Replace the word in the text
    processedText = processedText.replace(new RegExp(`\\b${oldWord}\\b`, 'gi'), newWord);
    // Remove the command itself
    processedText = processedText.replace(match[0], '');
  }
  
  return processedText;
};

// Format dictation text with punctuation and formatting
const formatDictationText = (text, editor) => {
  text = handleTextCommands(text);
  
  let formatted = text
    // Basic punctuation commands
    .replace(/\bperiod\b/gi, '. ')
    .replace(/\bcomma\b/gi, ', ')
    .replace(/\bquestion mark\b/gi, '? ')
    .replace(/\bexclamation point\b/gi, '! ')
    .replace(/\bcolon\b/gi, ': ')
    .replace(/\bsemicolon\b/gi, '; ')
    
    // Line breaks and formatting
    .replace(/\bnew line\b/gi, '<br>')
    .replace(/\bnew paragraph\b/gi, '</p><p>')
    
    // Voice command cleanup
    .replace(/\bdelete that\b/gi, '')
    .replace(/\bscratch that\b/gi, '')
    .replace(/\bundo\b/gi, '')
    
    // Clean up extra spaces
    .replace(/\s+/g, ' ')
    .replace(/\s+([.,:;!?])/g, '$1')
    .trim();
  
  // Capitalize first letter and after sentence-ending punctuation
  formatted = formatted.replace(/^\w|[.!?]\s+\w/g, match => match.toUpperCase());
  
  return formatted;
};

export const setupAWSMedicalSpeech = (editor) => {
  let recognition = null;
  let isListening = false;
  let dictationBuffer = '';
  let isDictating = false;
  let previewElement = null;
  let originalCursorPosition = null;
  let rawBuffer = ''; // Keep original unprocessed text
  let inactivityTimer = null;

  // Register button immediately
  editor.ui.registry.addToggleButton('medicalspeech', {
    text: '🎤',
    tooltip: 'Speech to Text',
    onAction: () => {
      if (isListening) {
        stopListening();
      } else {
        startListening();
      }
    },
    onSetup: (api) => {
      const updateState = () => {
        api.setActive(isListening);
      };
      editor.on('SpeechStateChange', updateState);
      return () => editor.off('SpeechStateChange', updateState);
    }
  });

  // Check credentials and setup AWS in background
  const checkCredentials = async () => {
    try {
      const credentials = await GetAWSCredentials();
      
      // Create transcribe client (not stored since not used elsewhere)
      new TranscribeStreamingClient({ 
        region: credentials.region, 
        credentials: { 
          accessKeyId: credentials.accessKeyId, 
          secretAccessKey: credentials.secretAccessKey 
        } 
      });
      
      // Update button icon to speaking person
      setTimeout(() => {
        const buttonElement = document.querySelector('[aria-label="Speech to Text"]');
        if (buttonElement) {
          const textSpan = buttonElement.querySelector('.tox-tbtn__select-label');
          if (textSpan) {
            textSpan.textContent = '🗣️';
            textSpan.style.color = 'white';
            textSpan.style.filter = 'brightness(0) invert(1)';
            textSpan.style.fontSize = '16px';
            textSpan.style.fontWeight = 'bold';
            buttonElement.setAttribute('aria-label', 'Medical Dictation (AWS)');
            buttonElement.setAttribute('title', 'Medical Dictation (AWS)');
          }
        }
      }, 100);
      
      return true;
    } catch (error) {
      return false;
    }
  };

  // Initialize speech recognition and check AWS
  const setupBrowserSpeech = () => {
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SpeechRecognition) return false;
    
    recognition = new SpeechRecognition();
    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.lang = 'en-US';
    
    recognition.onresult = (event) => {
      let finalTranscript = '';
      let interimTranscript = '';
      
      for (let i = event.resultIndex; i < event.results.length; i++) {
        const transcript = event.results[i][0].transcript;
        if (event.results[i].isFinal) {
          finalTranscript += transcript + ' ';
        } else {
          interimTranscript += transcript;
        }
      }
      
      // Reset inactivity timer on any speech activity
      if (finalTranscript || interimTranscript) {
        resetInactivityTimer();
      }
      
      if (isDictating) {
        // Add final transcript to buffer
        if (finalTranscript) {
          dictationBuffer += finalTranscript;
          rawBuffer += finalTranscript;
        }
        
        // Update live preview with buffered + interim text
        updatePreview((rawBuffer + interimTranscript).trim());
      } else if (finalTranscript) {
        // Process immediately if not in dictation mode
        const formattedText = formatDictationText(finalTranscript, editor);
        if (formattedText.trim()) {
          editor.insertContent(formattedText);
        }
      }
    };
    
    recognition.onerror = (event) => {
      stopListening();
    };
    
    recognition.onend = () => {
      if (isListening) {
        setTimeout(() => {
          if (isListening) {
            try {
              recognition.start();
            } catch (e) {
              stopListening();
            }
          }
        }, 100);
      }
    };
    
    return true;
  };

  // Setup browser speech and check AWS credentials
  setupBrowserSpeech();
  checkCredentials();

  const updatePreview = (rawText) => {
    if (!previewElement) {
      // Create preview element
      previewElement = document.createElement('span');
      previewElement.style.cssText = 'background: rgba(25, 118, 210, 0.2); border-radius: 3px; color: #1976d2;';
      previewElement.setAttribute('data-dictation-preview', 'true');
    }
    
    // Remove existing preview
    const existingPreview = editor.getBody().querySelector('[data-dictation-preview]');
    if (existingPreview) {
      existingPreview.remove();
    }
    
    // Re-process the ENTIRE raw buffer from scratch each time
    const formattedPreview = formatDictationTextPreview(rawText);
    
    // Only show preview if there's content
    if (formattedPreview.trim()) {
      previewElement.innerHTML = formattedPreview;
      editor.selection.getRng().insertNode(previewElement.cloneNode(true));
    }
  };
  
  // Use the main formatting function for preview (pass null as editor to skip execCommands)
  const formatDictationTextPreview = (text) => {
    return formatDictationText(text, null);
  };
  
  const clearPreview = () => {
    const existingPreview = editor.getBody().querySelector('[data-dictation-preview]');
    if (existingPreview) {
      existingPreview.remove();
    }
  };

  const resetInactivityTimer = () => {
    if (inactivityTimer) {
      clearTimeout(inactivityTimer);
    }
    inactivityTimer = setTimeout(() => {
      if (isListening) {
        stopListening();
      }
    }, 60000); // 1 minute timeout
  };

  const handleVisibilityChange = () => {
    if (document.hidden && isListening) {
      stopListening();
    }
  };

  const startListening = () => {
    if (!recognition) return;
    
    isListening = true;
    isDictating = true;
    dictationBuffer = '';
    rawBuffer = '';
    originalCursorPosition = editor.selection.getBookmark();
    recognition.start();
    editor.dispatch('SpeechStateChange');
    
    // Start inactivity timer
    resetInactivityTimer();
    
    // Add listeners
    document.addEventListener('click', handleClickOutside);
    document.addEventListener('visibilitychange', handleVisibilityChange);
    editor.on('click', stopListening);
  };
  
  const handleClickOutside = (event) => {
    if (!event.target.closest('.tox-toolbar')) {
      stopListening();
    }
  };

  const stopListening = () => {
    if (!recognition) return;
    
    isListening = false;
    recognition.stop();
    
    // Clear timers and remove listeners
    if (inactivityTimer) {
      clearTimeout(inactivityTimer);
      inactivityTimer = null;
    }
    document.removeEventListener('click', handleClickOutside);
    document.removeEventListener('visibilitychange', handleVisibilityChange);
    editor.off('click', stopListening);
    
    // Clear preview
    clearPreview();
    
    // Process the entire buffered dictation when stopping
    if (dictationBuffer.trim() && isDictating) {
      const formattedText = formatDictationText(dictationBuffer, editor);
      
      // Only restore cursor and insert if there's remaining text after processing commands
      if (formattedText.trim()) {
        if (originalCursorPosition) {
          editor.selection.moveToBookmark(originalCursorPosition);
        }
        editor.insertContent(formattedText);
      }
    }
    
    isDictating = false;
    dictationBuffer = '';
    rawBuffer = '';
    originalCursorPosition = null;
    editor.dispatch('SpeechStateChange');
  };
};